<template>
  <div ref="wrapper" class="relative bg-background">
    <div class="flex gap-6">
      <SmTabsItem
        v-for="tab in tabs"
        :key="tab.title"
        ref="items"
        :title="tab.title"
        :path="tab.path" />
    </div>
    <div
      v-show="indicatorVisible"
      :class="[
        'pointer-events-none absolute bottom-0 left-0 h-[1px] rounded bg-primary',
        shouldAnimate
          ? 'transition-[transform,width] duration-200 ease-out'
          : 'transition-none',
      ]"
      :style="indicatorStyle"></div>
  </div>
</template>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    watch,
    useTemplateRef,
  } from 'vue'
  import { useEventListener, useResizeObserver } from '@vueuse/core'

  import SmTabsItem from './SmTabsItem.vue'

  type SmTabsItemInstance = InstanceType<typeof SmTabsItem>

  const itemRefs = useTemplateRef<SmTabsItemInstance[]>('items')
  const wrapperRef = useTemplateRef<HTMLDivElement>('wrapper')

  const activeTab = computed(() => {
    return itemRefs.value?.find((item) => item.isActive)
  })

  const indicatorWidth = ref(0)
  const indicatorLeft = ref(0)
  const indicatorVisible = computed(() => indicatorWidth.value > 0)
  const shouldAnimate = ref(false)

  const updateIndicator = () => {
    const wrapperEl = wrapperRef.value
    const activeInstance = activeTab.value

    if (!wrapperEl || !activeInstance) {
      indicatorWidth.value = 0
      return
    }

    const activeEl = activeInstance.$el
    if (!(activeEl instanceof HTMLElement)) {
      return
    }

    const wrapperRect = wrapperEl.getBoundingClientRect()
    const activeRect = activeEl.getBoundingClientRect()

    indicatorWidth.value = activeRect.width
    indicatorLeft.value = activeRect.left - wrapperRect.left
  }

  const indicatorStyle = computed(() => ({
    width: `${indicatorWidth.value}px`,
    transform: `translate3d(${indicatorLeft.value}px, 1px, 0)`,
  }))

  let animationResetTimer: number | undefined
  let pendingAnimation = false
  // Only animate when a tab change triggers the update; layout-driven refreshes
  // keep the indicator locked to the active item without lag.
  const scheduleUpdate = (animate = false) => {
    if (animate) {
      pendingAnimation = true
      if (typeof window !== 'undefined') {
        shouldAnimate.value = true
        if (animationResetTimer !== undefined) {
          window.clearTimeout(animationResetTimer)
        }
        animationResetTimer = window.setTimeout(() => {
          shouldAnimate.value = false
          animationResetTimer = undefined
        }, 250)
      }
    } else if (!pendingAnimation && animationResetTimer === undefined) {
      shouldAnimate.value = false
    }

    const run = () => {
      updateIndicator()
      pendingAnimation = false
    }

    if (
      typeof window === 'undefined' ||
      typeof window.requestAnimationFrame === 'undefined'
    ) {
      run()
      return
    }

    window.requestAnimationFrame(run)
  }

  let initialized = false
  watch(
    () => activeTab.value,
    () => {
      nextTick(() => {
        scheduleUpdate(initialized)
        if (!initialized) {
          initialized = true
        }
      })
    },
    { immediate: true }
  )

  useResizeObserver(wrapperRef, () => scheduleUpdate())
  const activeElement = computed(
    () => activeTab.value?.$el as HTMLElement | undefined
  )
  useResizeObserver(activeElement, () => scheduleUpdate())

  if (typeof window !== 'undefined') {
    useEventListener(window, 'resize', () => scheduleUpdate())
  }

  onMounted(() => {
    scheduleUpdate()
  })

  onUnmounted(() => {
    if (typeof window !== 'undefined' && animationResetTimer !== undefined) {
      window.clearTimeout(animationResetTimer)
      animationResetTimer = undefined
    }
  })

  defineProps<{
    tabs: {
      title: string
      path: string
    }[]
  }>()
</script>
